/**
 * Shared user credentials model used across all auth providers.
 */
export interface IAuthUser {
  email: string;
  password: string;
}

/**
 * Abstract base class defining the contract for all authentication providers
 * (Cognito, Keycloak, etc.).  Concrete implementations are resolved at runtime
 * via the AUTH_SERVICE injection token based on the `authProvider` environment flag.
 */
export abstract class IAuthService {
  /**
   * Called once at application start (via APP_INITIALIZER).
   * Use this to bootstrap the provider SDK (e.g. keycloak.init()).
   * Cognito implementation is a no-op that resolves immediately.
   */
  abstract initialize(): Promise<boolean>;

  /** Sign in with the given credentials. */
  abstract signIn(user: IAuthUser): Promise<any>;

  /** Sign out the current user and clear session state. */
  abstract signOut(): Promise<any>;

  /** Returns true if there is an active, authenticated session. */
  abstract isAuthenticated(): Promise<boolean>;

  /** Returns the raw ID token string, or null if not authenticated. */
  abstract getIdToken(): string | null;

  /** Returns the raw access token string, or null if not authenticated. */
  abstract getAccessToken(): string | null;

  /**
   * Returns true if the current token is still valid.
   * Also triggers a background refresh when the token is close to expiry.
   */
  abstract isTokenValid(): boolean;

  /** Starts a periodic interval to proactively refresh tokens before expiry. */
  abstract startTokenRefreshInterval(): void;
  abstract getUser(): Promise<any>
}
